import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { AdConnectionBadge, DataBadge } from "@/components/nadia/Badges";
import { KPICard } from "@/components/nadia/KPICard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { listCampaigns, getChannels, getAdConnections, syncMeta, getVideo } from "@/lib/api";
import { CampaignReel } from "@/components/nadia/CampaignReel";
import { Megaphone, MousePointerClick, Users, Wallet, Target, RefreshCw, Zap, Radio, Loader2 } from "lucide-react";

const PUBLICATION_LABELS = new Map([
  ["preparato", "Preparata · invio non confermato"],
  ["approvato", "Approvata · pubblicazione non confermata"],
  ["inviato", "Richiesta inviata · attività non confermata"],
  ["confermato_dal_provider", "Creazione confermata · attività non confermata"],
  ["attivo", "Attività dichiarata dall'API"],
  ["bloccato", "Operazione bloccata"],
  ["incerto", "Esito incompleto · incerto"],
  ["parziale", "Esito incompleto · parziale"],
  ["storico_non_verificato", "Stato storico non verificato"],
]);

const CHANNEL_ICONS = {
  meta_ads: Megaphone,
  instagram: Radio,
  facebook: Radio,
  whatsapp: Zap,
  email: Zap,
  google_ads: Radio,
  tiktok: Radio,
};

const AD_CHANNELS = [
  { id: "meta_ads", name: "Meta Ads", provider: "meta" },
  { id: "instagram", name: "Instagram", provider: "meta" },
  { id: "facebook", name: "Facebook", provider: "meta" },
  { id: "google_ads", name: "Google Ads", provider: "google" },
  { id: "tiktok", name: "TikTok Ads", provider: "tiktok" },
];

export default function Campagne() {
  const [camps, setCamps] = useState(null);
  const [channels, setChannels] = useState([]);
  const [adConnections, setAdConnections] = useState(null);
  const [adConnectionsError, setAdConnectionsError] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [reel, setReel] = useState(null);

  const load = () => {
    listCampaigns().then(setCamps).catch(() => setCamps([]));
    getVideo().then(setReel).catch(() => setReel(null));
    getChannels().then((rows) => setChannels(rows.filter((ch) => !AD_CHANNELS.some((ad) => ad.id === ch.id)))).catch(() => setChannels([]));
    setAdConnectionsError(false);
    getAdConnections().then(setAdConnections).catch(() => { setAdConnections(null); setAdConnectionsError(true); });
  };
  useEffect(() => { load(); }, []);

  const doSync = async (id) => {
    setSyncing(id);
    try {
      const res = await syncMeta(id);
      toast.success(
        res.mode === "live"
          ? "Metriche aggiornate da Meta Ads"
          : "Meta Ads non configurato: metriche non aggiornate"
      );
      load();
    } catch {
      toast.error("Errore nel sync");
    } finally {
      setSyncing(null);
    }
  };

  if (!camps) return <Skeleton className="h-96 bg-white/5" />;

  return (
    <>
      <PageHeader
        eyebrow="Campagne"
        title="Le tue campagne"
        subtitle="Meta Ads è solo uno dei miei strumenti. L'obiettivo di business viene prima."
      />

      {camps.filter((c) => c.kind === "approved_campaign_draft").map((c) => (
        <section key={c.id} className="nadia-card p-6 mb-6" data-testid={`campaign-draft-${c.id}`}>
          <p className="text-xs text-cyan-300 mb-2" data-testid={`campaign-draft-status-${c.id}`}>Bozza locale · non pubblicata</p>
          <h2 className="font-heading text-lg font-semibold text-slate-50 mb-2" data-testid={`campaign-draft-name-${c.id}`}>{c.name}</h2>
          <p className="text-xs text-slate-400 break-all mb-4" data-testid={`campaign-draft-id-${c.id}`}>ID: {c.id}</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["channel", "Canale"], ["budget_note", "Budget concordato"], ["objective", "Obiettivo"], ["promotion", "Promozione"]].map(([key, label]) => (
              <div key={key}>
                <dt className="text-xs text-slate-400 mb-1" data-testid={`campaign-draft-${c.id}-${key}-label`}>{label}</dt>
                <dd className="text-sm text-slate-100 whitespace-pre-wrap break-words" data-testid={`campaign-draft-${c.id}-${key}`}>{c[key]}</dd>
              </div>
            ))}
          </dl>
          <DraftBudgetDetails campaign={c} />
          <DraftKeywordDetails campaign={c} />
          <DraftCopyDetails campaign={c} />
              <Link className="inline-block mt-4 rounded-lg border border-cyan-400/30 px-4 py-2 text-sm text-cyan-200" to={`/app/nadia?campaign=${encodeURIComponent(c.id)}`}>Rivedi creatività e pubblicazione</Link>
          {c.material && <p className="text-sm text-slate-200 whitespace-pre-wrap mt-4" data-testid={`campaign-draft-material-${c.id}`}>{c.material}</p>}
          <p className="text-xs text-slate-400 mt-4" data-testid={`campaign-draft-scope-${c.id}`}>Salvata dalla versione approvata. Nessuna campagna inviata ai canali, nessuna spesa attivata. Pubblicazione e spesa richiedono un'approvazione separata.</p>
        </section>
      ))}

      {camps.filter((c) => c.kind !== "approved_campaign_draft").map((c) => (
        <div key={c.id} className="nadia-card p-6 mb-6" data-testid={`campaign-card-${c.id}`}>
          <div className="flex flex-col md:flex-row gap-6">
            <img
              src={c.creative_url}
              alt=""
              className="w-full md:w-48 h-48 md:h-40 object-cover rounded-2xl border border-white/10"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold">{c.channel}</span>
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/5 text-slate-300 border border-white/10" data-testid={`campaign-publication-state-${c.id}`}>
                  {PUBLICATION_LABELS.get(c.publication_state) || "Stato operativo non confermato"}
                </span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-50 mb-2">{c.name}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Info label="Obiettivo" value={c.objective} />
                <Info label="Target" value={c.target} />
                <Info label="Area" value={c.geographic_area} />
                <Info label="Budget/mese" value={`€${c.budget_monthly}`} />
              </div>
            </div>
          </div>

          <CampaignReel project={reel} onChange={(p) => { setReel(p); load(); }} />

          <div className="flex items-center justify-between gap-2 mt-6 mb-3">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-sm font-semibold text-slate-100">Risultati</h3>
              <DataBadge variant="real" />
            </div>
            <Button
              onClick={() => doSync(c.id)}
              disabled={syncing === c.id}
              variant="outline"
              size="sm"
              className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent h-8"
              data-testid={`sync-meta-${c.id}`}
            >
              {syncing === c.id ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : (
                <RefreshCw size={13} className="mr-1.5" />
              )}
              Aggiorna da Meta
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard testId={`c-${c.id}-spend`} label="Spesa" value={`€${c.spend}`} icon={Wallet} />
            <KPICard testId={`c-${c.id}-clicks`} label="Click reali" value={c.metrics.clicks} icon={MousePointerClick} />
            <KPICard testId={`c-${c.id}-conv`} label="Prenotazioni" value={c.metrics.conversions} icon={Users} />
            <KPICard testId={`c-${c.id}-cpa`} label="Costo per risultato" value={`€${c.metrics.cpa}`} icon={Target} />
          </div>
        </div>
      ))}

      <PageHeader title="Canali" subtitle="Nadia può orchestrare campagne su questi canali." />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[...AD_CHANNELS, ...channels].map((ch) => {
          const Icon = CHANNEL_ICONS[ch.id] || Radio;
          const connection = ch.provider ? adConnections?.[ch.provider] : null;
          const active = ch.provider
            ? connection?.status === "connected" && !connection.accounts_error && !connection.pages_error && (ch.provider !== "meta" || connection.publish_ready)
            : ch.status === "active";
          const soon = !ch.provider && ch.status === "coming_soon";
          return (
            <div key={ch.id} className="nadia-card p-4 flex items-center gap-3" data-testid={`channel-${ch.id}`}>
              <span
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  active
                    ? "bg-cyan-500/10 border border-cyan-500/25 text-cyan-300"
                    : "bg-white/[0.03] border border-white/10 text-slate-500"
                }`}
              >
                <Icon size={17} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-heading font-semibold text-slate-100 truncate">{ch.name}</div>
                {ch.provider ? (
                  <>
                    <AdConnectionBadge connection={connection} loading={!adConnections && !adConnectionsError} error={adConnectionsError} testId={`channel-status-${ch.id}`} />
                    {connection?.accounts_error && <p className="text-[11px] text-amber-300 break-words" data-testid={`channel-accounts-error-${ch.id}`}>Account pubblicitari: {connection.accounts_error}</p>}
                    {connection?.pages_error && <p className="text-[11px] text-amber-300 break-words" data-testid={`channel-pages-error-${ch.id}`}>Pagine Facebook: {connection.pages_error}</p>}
                  </>
                ) : (
                  <div className="text-[11px] text-slate-400" data-testid={`channel-status-${ch.id}`}>
                    {active ? "Connesso" : soon ? "Prossimamente" : "Disponibile"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-200 font-medium">{value}</div>
    </div>
  );
}


function DraftBudgetDetails({ campaign }) {
  const fields = [
    ["declared_budget", "Budget totale dichiarato"],
    ["daily_budget_suggestion", "Budget giornaliero suggerito"],
    ["duration_days", "Durata"],
  ].filter(([key]) => campaign[key] !== null && campaign[key] !== undefined && campaign[key] !== "");
  if (!fields.length) return null;

  return (
    <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/10 pt-4" data-testid={`campaign-draft-budget-details-${campaign.id}`}>
      {fields.map(([key, label]) => (
        <div key={key}>
          <dt className="text-xs text-slate-400 mb-1" data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}-label`}>{label}</dt>
          <dd className="text-sm text-slate-100 whitespace-pre-wrap break-words" data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}`}>
            {key === "duration_days" ? `${campaign[key]} ${campaign[key] === 1 ? "giorno" : "giorni"}` : formatDraftBudget(campaign[key])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatDraftBudget(value) {
  if (value && typeof value === "object") {
    if (typeof value.amount === "number" || typeof value.amount === "string") {
      return [value.amount, value.currency === "EUR" ? "€" : value.currency, value.period]
        .filter((part) => part !== null && part !== undefined && part !== "").join(" ");
    }
    return JSON.stringify(value);
  }
  return String(value);
}


function DraftKeywordDetails({ campaign }) {
  const fields = [
    ["keywords_phrase", "Keyword a frase"],
    ["keywords_exact", "Keyword esatte"],
    ["keywords_negative", "Keyword negative"],
  ].filter(([key]) => Array.isArray(campaign[key]) && campaign[key].every((term) => typeof term === "string"));
  if (!fields.length) return null;

  return (
    <section className="mt-4 border-t border-white/10 pt-4" aria-labelledby={`campaign-keywords-title-${campaign.id}`} data-testid={`campaign-draft-keywords-${campaign.id}`}>
      <h3 id={`campaign-keywords-title-${campaign.id}`} className="font-heading text-sm font-semibold text-slate-100 mb-3" data-testid={`campaign-draft-keywords-title-${campaign.id}`}>Keyword della bozza</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {fields.map(([key, label]) => (
          <div key={key}>
            <h4 className="text-xs text-slate-400 mb-2" data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}-label`}>{label}</h4>
            {campaign[key].length ? (
              <ul className="list-disc pl-4 space-y-1 text-sm text-slate-100 break-words" data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}`}>
                {campaign[key].map((term, index) => <li key={index} data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}-${index}`}>{term}</li>)}
              </ul>
            ) : <p className="text-xs text-slate-400" data-testid={`campaign-draft-${campaign.id}-${key.replaceAll("_", "-")}-empty`}>Elenco vuoto</p>}
          </div>
        ))}
      </div>
    </section>
  );
}


function DraftCopyDetails({ campaign }) {
  const fields = [
    ["headlines", "Titoli"],
    ["descriptions", "Descrizioni"],
  ].filter(([key]) => Array.isArray(campaign[key]) && campaign[key].every((text) => typeof text === "string"));
  if (!fields.length) return null;

  return (
    <section className="mt-4 border-t border-white/10 pt-4" aria-labelledby={`campaign-copy-title-${campaign.id}`} data-testid={`campaign-draft-copy-${campaign.id}`}>
      <h3 id={`campaign-copy-title-${campaign.id}`} className="font-heading text-sm font-semibold text-slate-100 mb-3" data-testid={`campaign-draft-copy-title-${campaign.id}`}>Titoli e descrizioni</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([key, label]) => (
          <div key={key}>
            <h4 className="text-xs text-slate-400 mb-2" data-testid={`campaign-draft-${campaign.id}-${key}-label`}>{label}</h4>
            {campaign[key].length ? (
              <ol className="list-decimal pl-4 space-y-1 text-sm text-slate-100 whitespace-pre-wrap break-words" data-testid={`campaign-draft-${campaign.id}-${key}`}>
                {campaign[key].map((text, index) => <li key={index} data-testid={`campaign-draft-${campaign.id}-${key}-${index}`}>{text}</li>)}
              </ol>
            ) : <p className="text-xs text-slate-400" data-testid={`campaign-draft-${campaign.id}-${key}-empty`}>Elenco vuoto</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
